import { Product } from '@shared/components/ui/Product';
import { RenderItemProps } from '@shared/components/ui/VirtualList';
import { faLock } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { getPrimaryDisplayableStockBatch, Location } from '../../models/Location';
import { DropdownActions } from '../interaction/DropdownActions';
import { LocationBatchBadge } from './LocationBatchBadge';

// 🎯 Props para LocationCard incluyendo las acciones
export interface LocationCardProps extends RenderItemProps<Location> {
  onEdit?: (location: Location) => void;
  onDelete?: (location: Location) => void;
  onAdjust?: (location: Location) => void;
  onPrint?: (location: Location, size: 'small' | 'large') => void;
  onToggleLock?: (location: Location) => void;
  className?: string;
  showActions?: boolean;
  showIndex?: boolean;
}

// 🎯 Componente de card individual para Location - SRP
export const LocationCard = ({
  item: location,
  onEdit,
  onDelete,
  onAdjust,
  onPrint,
  onToggleLock,
  className = '',
  showActions = true,
}: LocationCardProps) => {
  const { t } = useTranslation('locationsModule');
  const primaryBatch = getPrimaryDisplayableStockBatch(location);

  // 🎯 Función para obtener la traducción del tipo de ubicación
  const getLocationTypeTranslation = (typeName: string | undefined) => {
    if (!typeName) return t('location_types.not_available');

    // Mapear los tipos de la API a las claves de traducción
    const typeMap: Record<string, string> = {
      Almacen: 'location_types.warehouse',
      Almacén: 'location_types.warehouse',
      Warehouse: 'location_types.warehouse',
      Picking: 'location_types.picking',
    };

    const translationKey = typeMap[typeName];
    return translationKey ? t(translationKey) : typeName;
  };

  return (
    <div className={`py-4 border-b border-card-border hover:bg-muted/30 transition-colors ${className}`}>
      <div className="space-y-4">
        {/* 🎯 Header con código y tipo */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {location.is_locked && (
              <FontAwesomeIcon icon={faLock} className="text-muted-foreground text-sm" title={t('status.locked')} />
            )}
            <span className="font-medium text-foreground text-base">{location.code}</span>
            {location.type && (
              <span className="text-xs bg-muted px-2 py-1 rounded-full text-muted-foreground capitalize">
                {getLocationTypeTranslation(location.type.name)}
              </span>
            )}
          </div>

          {/* 🎯 Dropdown actions (opcional) */}
          {showActions && (
            <DropdownActions
              location={location}
              onEdit={onEdit}
              onDelete={onDelete}
              onAdjust={onAdjust}
              onPrint={onPrint}
              onToggleLock={onToggleLock}
            />
          )}
        </div>

        {/* 🎯 Producto (si existe) */}
        {location.product?.name ? (
          <div className="flex items-start">
            <Product
              src={location.product.image || ''}
              name={location.product.name}
              id={location.product.id}
              code={location.product.code || ''}
              className="flex-shrink-0"
              showImageModal={true}
            />
          </div>
        ) : (
          <div className="flex items-center">
            <span className="text-sm text-muted-foreground">{t('card.no_product')}</span>
          </div>
        )}

        {/* 🎯 Stock y información adicional (solo si hay producto) */}
        {location.product?.name && (
          <div className={`flex flex-col gap-3 ${primaryBatch ? 'sm:flex-row sm:items-start sm:justify-between' : ''}`}>
            <div className="flex items-center gap-4">
              <div className="text-sm">
                <span className="text-muted-foreground">{t('card.stock_label')} </span>
                <span className="font-medium text-foreground">{location.stock || 0}</span>
              </div>
            </div>
            {primaryBatch ? (
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground">{t('table.batch')}</p>
                <LocationBatchBadge batch={primaryBatch} className="w-full sm:max-w-md" />
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};
